<template lang="pug">
VContainer(:fluid="true")
  VvBreadcrumbs(:breadcrumbs="breadcrumbs")

  VSkeletonLoader.pt-8.pb-8(
    v-if="bookQuery.isPending.value"
    type="heading"
  )
  div.mt-8.text-center(v-else)
    h1.text-h1.mb-3 {{ bookName }}
    p {{ i18n.t("ScriptureIndex.bibleBookDescription", { name: bookName }) }}

  VBtn.mt-4(
    :color="dark ? 'primary-c050' : 'primary-c600'"
    :to="localePath(getScriptureIndexBooksLocation())"
    variant="outlined"
  )
    VIcon.mr-2 {{ mdiArrowLeft }}
    | {{ i18n.t("ScriptureIndex.ScriptureIndex") }}

  IndexListChapters.px-4.mt-4(
    :local-tid="localTid"
    :bid="bid"
  )
</template>

<script setup lang="ts">
import { allBids, type BookId } from "~/units/scripture";
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { mdiArrowLeft } from "@mdi/js";
import { useQuery } from "@tanstack/vue-query";
import { injectRequired } from "~/injectRequired";
import { getStringParam } from "~/routing/routes";
import { getScriptureIndexBooksLocation } from "~/routing/locations";
import { LogFunctionKey, ScriptureClientKey } from "~/injectionKeys";
import { localesByCode } from "~/i18n/locales";
import { never } from "~/never";
import { makeResultAsyncSerializable } from "~/errors/util";
import { useDarkMode } from "~/composables/useDarkMode";

const i18n = useI18n();
const scriptureClient = injectRequired(ScriptureClientKey);
const log = injectRequired(LogFunctionKey);

const route = useRoute();
const localePath = useLocalePath();
const dark = useDarkMode();

const localeConfig = computed(
  () => localesByCode.get(i18n.locale.value) ?? never("Locale not found."),
);
const localTid = computed<string>(() => localeConfig.value.defaultTid);

const breadcrumbs = computed(() => [
  {
    to: getScriptureIndexBooksLocation(),
    title: i18n.t("ScriptureIndex.ScriptureIndex"),
  },
]);

// The fallback is not expected to happen, since the `bid` param is required.
const bid = computed<BookId>(() => {
  const value = getStringParam(route, "bid");
  const found = allBids.find((b) => b === value);
  if (!found)
    throw createError({ statusCode: 404, statusMessage: "Unknown Bible book" });
  return found;
});

// TODO: Do something with the error from this query.
const bookQuery = useQuery({
  queryKey: ["scriptureClient", "getBook", localTid, bid],
  queryFn: () =>
    makeResultAsyncSerializable(
      () => scriptureClient.getBook(localTid.value, bid.value),
      log,
    ),
});

const bookName = computed<string>(
  () => bookQuery.data.value?.value?.name ?? "…",
);

useHead({
  title: () => `Scripture index > ${bookName.value}`,
});

onServerPrefetch(async () => {
  await bookQuery.suspense();
});
</script>
